// WGSL for brush and knife material exchange on the GPU. Mirrors
// brushes/StampExchange.js (exchangeStamp) and brushes/PaletteKnife.js
// (applyBlade). Bristle kinematics stay on the CPU (O(bristle groups) per
// step); every per-cell operation runs here. Stamps are processed in stroke
// order by one workgroup (each stamp's pickup depends on the previous one);
// each stamp's footprint is processed in parallel by the whole workgroup,
// which is as large as the device allows (see brushKernelConfig).

import { PIGMENT_SLOTS, TILE_SIZE } from '../core/PaintState.js';
import { BODY_PRESENT } from '../physics/ViscousPaint.js';
import { LOAD_REACH, TIP_EXCHANGE_LENGTH, TIP_BACKFLOW, STAMP_STRIDE } from '../brushes/StampExchange.js';
import { FIELD } from './simShader.js';

export const CARRIER_STRIDE = 20; // water, body, capacity, pad, pigW[8], pigB[8]
export const KNIFE_STEP_STRIDE = 8; // cx, cy, pressure, ds, dirX, dirY, pad, pad
export const MAX_FOOTPRINT = 8192;
// Threads cooperating on one stamp's footprint without subgroup operations
// (shared memory: partial sums BRUSH_THREADS x 18 plus all bristle carriers
// must fit in 32 KB).
export const BRUSH_THREADS = 192;
const WORKGROUP_STORAGE_BUDGET = 32768;
// Bristle groups held in workgroup memory (presets use at most 70).
export const MAX_GROUPS = 96;
export const KNIFE_SKIN = 0.12;

const f = (v) => {
  const s = Number(v).toString();
  return /[.eE]/.test(s) ? s : `${s}.0`;
};

// Workgroup size and reduction mode for a device: with subgroup operations
// the partial sums need one slot per subgroup, so the workgroup can be as
// large as the device allows (up to 1024 threads).
export function brushKernelConfig({ subgroups = false, minSubgroupSize = 4, maxInvocations = 256, threads: wanted = 1024 } = {}) {
  if (!subgroups) {
    const t = Math.min(BRUSH_THREADS, wanted);
    return { threads: t, subgroups: false, partialSlots: t };
  }
  const fixed = 4 * (MAX_GROUPS * 2 * CARRIER_STRIDE + CARRIER_STRIDE + 4 + 64);
  let threads = wanted;
  while (threads > 64 && (threads > maxInvocations || fixed + 4 * 18 * Math.ceil(threads / Math.max(1, minSubgroupSize)) > WORKGROUP_STORAGE_BUDGET)) threads /= 2;
  return { threads, subgroups: true, partialSlots: Math.ceil(threads / Math.max(1, minSubgroupSize)) };
}

// Kernel tiers by footprint size: [{ maxCells, config }], smallest first.
// maxCells bounds the base footprint (bounding box before wet spread).
// Measured on an RTX 5060 Ti: tiny footprints (small brushes) are fastest on
// a 128-thread kernel with shared-memory sums; anything larger on the
// largest workgroup the device allows.
export function brushKernelTiers(device = {}) {
  return [
    { maxCells: 64, config: { threads: 128, subgroups: false, partialSlots: 128 } },
    { maxCells: Infinity, config: brushKernelConfig(device) },
  ];
}

export function buildBrushShader(config = brushKernelConfig()) {
  const F = FIELD;
  // Shadows the module constant: the template below uses the device's size.
  // eslint-disable-next-line no-shadow
  const BRUSH_THREADS = config.threads;
  const SG = config.subgroups;
  // Partial sums: one slot per thread, or one per subgroup.
  const PART = config.partialSlots;
  return /* wgsl */ `${SG ? 'enable subgroups;' : ''}
struct Params {
  W: u32, H: u32, tilesX: u32, tilesY: u32,
  dt: f32, tiltX: f32, tiltY: f32, evapScale: f32,
  dryingScale: f32, slotsMask: u32, surfaceSheen: f32, granulationScale: f32,
};
struct Stroke {
  radius: f32, release: f32, stiffness: f32, plough: f32,
  pickupScale: f32, tooth: f32, count: u32, kind: u32,
  rewet: f32, mobility: f32, yieldStress: f32, bodyDrying: f32,
  heightGain: f32, wetGloss: f32, bladeLength: f32, bladeAngle: f32,
  flex: f32, scrape: f32, spread: f32, edgeRidge: f32,
};

@group(0) @binding(0) var<uniform> prm: Params;
@group(0) @binding(1) var<storage, read_write> S: array<f32>;
@group(0) @binding(2) var<storage, read_write> P: array<f32>;
@group(0) @binding(3) var<storage, read> surf: array<f32>;
@group(0) @binding(4) var<storage, read_write> T: array<atomic<u32>>;
@group(0) @binding(5) var<uniform> stroke: Stroke;
@group(0) @binding(6) var<storage, read_write> C: array<f32>;
@group(0) @binding(7) var<storage, read> stamps: array<f32>;
@group(0) @binding(8) var<storage, read_write> fw: array<f32>;
// UV island of each cell (3D surface painting); read only for stamps with an island.
@group(0) @binding(9) var<storage, read> isl: array<u32>;

const SLOTS: u32 = ${PIGMENT_SLOTS}u;
const TILE: u32 = ${TILE_SIZE}u;
const CR: u32 = ${CARRIER_STRIDE}u;
const BODY_PRESENT: f32 = ${f(BODY_PRESENT)};
const MAXF: u32 = ${MAX_FOOTPRINT}u;

fn N() -> u32 { return prm.W * prm.H; }
fn nt() -> u32 { return prm.tilesX * prm.tilesY; }
fn sf(field: u32, i: u32) -> f32 { return S[field * N() + i]; }
fn sset(field: u32, i: u32, v: f32) { S[field * N() + i] = v; }
fn pi(layer: u32, k: u32, i: u32) -> u32 { return (layer * SLOTS + k) * N() + i; }
// Pigment slots in use (unused slots hold no pigment and are skipped).
fn slotOn(k: u32) -> bool { return (prm.slotsMask & (1u << k)) != 0u; }

fn smoothstep3(a: f32, b: f32, x: f32) -> f32 {
  let t = clamp((x - a) / (b - a), 0.0, 1.0);
  return t * t * (3.0 - 2.0 * t);
}

// core/PaintState.markRect (active + dirty).
fn markRect(x0: i32, y0: i32, x1: i32, y1: i32) {
  let tx0 = max(0, x0 / i32(TILE));
  let ty0 = max(0, y0 / i32(TILE));
  let tx1 = min(i32(prm.tilesX) - 1, x1 / i32(TILE));
  let ty1 = min(i32(prm.tilesY) - 1, y1 / i32(TILE));
  for (var ty = ty0; ty <= ty1; ty++) {
    for (var tx = tx0; tx <= tx1; tx++) {
      let t = u32(ty) * prm.tilesX + u32(tx);
      atomicStore(&T[2u * nt() + t], 1u);
      atomicStore(&T[t], 1u);
    }
  }
}

// PaintDeposition.transferCarrier between carrier records at offsets a -> b.
fn transferCarrier(a: u32, b: u32, frac0: f32) {
  let frac = clamp(frac0, 0.0, 1.0);
  if (frac <= 0.0) { return; }
  let room = max(0.0, C[b + 2u] - C[b] - C[b + 1u]);
  let amount = (C[a] + C[a + 1u]) * frac;
  var scale = frac;
  if (amount > room) { scale = (room / max(1e-12, amount)) * frac; }
  if (scale <= 0.0) { return; }
  for (var q = 0u; q < 2u; q++) {
    let v = C[a + q] * scale;
    C[a + q] -= v;
    C[b + q] += v;
  }
  for (var q = 4u; q < 4u + 2u * SLOTS; q++) {
    let v = C[a + q] * scale;
    C[a + q] -= v;
    C[b + q] += v;
  }
}

// PaintDeposition.displaceBody
fn displaceBody(i: u32, j: u32, f0: f32) {
  let vi = sf(${F.bodyVol}u, i);
  if (f0 <= 0.0 || vi <= BODY_PRESENT) { return; }
  let frac = min(1.0, f0 * sf(${F.bodyOpen}u, i));
  let moved = vi * frac;
  if (moved <= 0.0) { return; }
  let wNew = moved / (sf(${F.bodyVol}u, j) + moved);
  var fields = array<u32, 6>(${F.bodyMob}u, ${F.bodyYield}u, ${F.bodyDry}u, ${F.bodySolids}u, ${F.bodyGloss}u, ${F.bodyOpen}u);
  for (var q = 0u; q < 6u; q++) {
    let fld = fields[q];
    sset(fld, j, sf(fld, j) + (sf(fld, i) - sf(fld, j)) * wNew);
  }
  sset(${F.bodyVol}u, i, vi - moved);
  sset(${F.bodyVol}u, j, sf(${F.bodyVol}u, j) + moved);
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    let a = P[pi(2u, k, i)] * frac;
    if (a > 0.0) { P[pi(2u, k, i)] -= a; P[pi(2u, k, j)] += a; }
  }
}

// Medium properties applied when wet body paint arrives at cell i.
fn mixBody(i: u32, b: f32) {
  let after = sf(${F.bodyVol}u, i) + b;
  let wNew = b / after;
  sset(${F.bodyMob}u, i, sf(${F.bodyMob}u, i) + (stroke.mobility - sf(${F.bodyMob}u, i)) * wNew);
  sset(${F.bodyYield}u, i, sf(${F.bodyYield}u, i) + (stroke.yieldStress - sf(${F.bodyYield}u, i)) * wNew);
  sset(${F.bodyDry}u, i, sf(${F.bodyDry}u, i) + (stroke.bodyDrying - sf(${F.bodyDry}u, i)) * wNew);
  sset(${F.bodySolids}u, i, sf(${F.bodySolids}u, i) + (stroke.heightGain - sf(${F.bodySolids}u, i)) * wNew);
  sset(${F.bodyGloss}u, i, sf(${F.bodyGloss}u, i) + (stroke.wetGloss - sf(${F.bodyGloss}u, i)) * wNew);
  sset(${F.bodyOpen}u, i, sf(${F.bodyOpen}u, i) + (1.0 - sf(${F.bodyOpen}u, i)) * wNew);
  sset(${F.bodyVol}u, i, after);
}

// ---------------------------------------------------------------- bristle stamps
struct StampState {
  px: f32, py: f32, r: f32, r2: f32,
  x0: i32, y0: i32, bw: i32, cells: i32,
  edgeContact: f32, reach: f32, speedSkip: f32, ds: f32,
  belly: u32, tip: u32, pad: u32, tipRelease: f32,
  weightSum: f32, contactSum: f32, pickScale: f32, keep: f32,
  island: u32, cover: f32,
};
var<workgroup> st: StampState;
// Barrier-guarding flag; read with workgroupUniformLoad so control flow stays uniform.
var<workgroup> skipFlag: u32;
var<workgroup> partial: array<f32, ${PART * 18}>;
var<workgroup> snap: array<f32, ${CARRIER_STRIDE}>;
var<workgroup> sums4: array<f32, 4>;
// All bristle carriers (belly + tip per group) stay in workgroup memory for
// the whole dispatch: thread 0's dependent carrier updates then cost shared
// memory latency instead of global memory latency.
var<workgroup> WC: array<f32, ${MAX_GROUPS * 2 * CARRIER_STRIDE}>;

fn transferWC(a: u32, b: u32, frac0: f32) {
  let frac = clamp(frac0, 0.0, 1.0);
  if (frac <= 0.0) { return; }
  let room = max(0.0, WC[b + 2u] - WC[b] - WC[b + 1u]);
  let amount = (WC[a] + WC[a + 1u]) * frac;
  var scale = frac;
  if (amount > room) { scale = (room / max(1e-12, amount)) * frac; }
  if (scale <= 0.0) { return; }
  for (var q = 0u; q < 2u; q++) {
    let v = WC[a + q] * scale;
    WC[a + q] -= v;
    WC[b + q] += v;
  }
  for (var q = 4u; q < 4u + 2u * SLOTS; q++) {
    let v = WC[a + q] * scale;
    WC[a + q] -= v;
    WC[b + q] += v;
  }
}

fn cellOf(c: i32) -> u32 {
  let x = st.x0 + c % st.bw;
  let y = st.y0 + c / st.bw;
  return u32(y) * prm.W + u32(x);
}

// Weight (falloff x contact) and falloff of footprint cell c, from current cell values.
fn footprint(c: i32) -> vec2<f32> {
  let x = st.x0 + c % st.bw;
  let y = st.y0 + c / st.bw;
  let ddx = f32(x) + 0.5 - st.px;
  let ddy = f32(y) + 0.5 - st.py;
  let d2 = ddx * ddx + ddy * ddy;
  if (d2 >= st.r2) { return vec2<f32>(0.0, 0.0); }
  let i = u32(y) * prm.W + u32(x);
  // A stamp mapped onto a UV island never reaches cells of another island
  // (or the empty gutter between islands).
  if (st.island != 0u && isl[i] != st.island) { return vec2<f32>(0.0, 0.0); }
  let falloff = (1.0 - d2 / st.r2) * (1.0 - d2 / st.r2);
  let inPaint = sf(${F.bodyVol}u, i) > 0.05 || sf(${F.water}u, i) > 0.05;
  let depth = (1.0 - surf[i]) * stroke.tooth;
  var contact = st.edgeContact;
  if (!inPaint) { contact = st.edgeContact * smoothstep3(depth - 0.15, depth + 0.2, st.reach) * st.speedSkip; }
  if (contact <= 0.005) { return vec2<f32>(0.0, falloff); }
  return vec2<f32>(falloff * contact, falloff);
}

@compute @workgroup_size(${BRUSH_THREADS})
fn brushStamps(@builtin(local_invocation_id) lid3: vec3<u32>${SG ? ', @builtin(subgroup_invocation_id) sgLane: u32, @builtin(subgroup_size) sgSize: u32' : ''}) {
  let lid = lid3.x;
  ${SG ? `// Partial-sum slot of this thread's subgroup, and the number of subgroups.
  let slot = lid / sgSize;
  let nslots = (${BRUSH_THREADS}u + sgSize - 1u) / sgSize;` : `let slot = lid;
  let nslots = ${BRUSH_THREADS}u;`}
  for (var q = lid; q < ${MAX_GROUPS * 2 * CARRIER_STRIDE}u; q += ${BRUSH_THREADS}u) { WC[q] = C[q]; }
  workgroupBarrier();
  for (var s = 0u; s < stroke.count; s++) {
    let o = s * ${STAMP_STRIDE}u;
    if (lid == 0u) {
      let g = u32(stamps[o]);
      st.belly = g * 2u * CR;
      st.tip = (g * 2u + 1u) * CR;
      let tipW = WC[st.tip];
      let tipB = WC[st.tip + 1u];
      let fullness = min(1.0, (tipW + tipB) / max(1e-6, WC[st.tip + 2u]));
      let liquid = tipW / max(1e-9, tipW + tipB);
      var r = stamps[o + 3u];
      if (stamps[o + 4u] <= 0.0) { r = r * (1.0 + 1.3 * liquid * min(1.0, fullness * 3.0) * (1.0 - stroke.stiffness * 0.5)); }
      st.px = stamps[o + 1u];
      st.py = stamps[o + 2u];
      st.r = r;
      st.r2 = r * r;
      st.edgeContact = stamps[o + 5u];
      let pe = stamps[o + 6u];
      st.ds = stamps[o + 7u];
      st.speedSkip = stamps[o + 8u];
      st.island = u32(stamps[o + 13u]);
      // Share of the stamp's exchange on this island copy (3D seams); 1 when unset.
      st.cover = select(1.0, stamps[o + 14u], stamps[o + 14u] > 0.0);
      st.reach = pe * 1.3 + fullness * 0.7 * (1.0 - stroke.stiffness * 0.3);
      let cx0 = max(0, i32(floor(st.px - r)));
      let cy0 = max(0, i32(floor(st.py - r)));
      let cx1 = min(i32(prm.W) - 1, i32(ceil(st.px + r)));
      let cy1 = min(i32(prm.H) - 1, i32(ceil(st.py + r)));
      st.x0 = cx0;
      st.y0 = cy0;
      st.bw = cx1 - cx0 + 1;
      st.cells = select(0, (cx1 - cx0 + 1) * (cy1 - cy0 + 1), cx0 <= cx1 && cy0 <= cy1);
      st.cells = min(st.cells, i32(MAXF));
      skipFlag = select(0u, 1u, st.cells <= 0);
    }
    if (workgroupUniformLoad(&skipFlag) == 1u) { continue; }

    // 1. Footprint weights (cached) and sums.
    var ws = 0.0;
    var cs = 0.0;
    var want = 0.0;
    var touched = 0.0;
    for (var c = i32(lid); c < st.cells; c += ${BRUSH_THREADS}) {
      let fp = footprint(c);
      let i = cellOf(c);
      ws += fp.y;
      fw[u32(c) * 3u] = fp.x;
      if (fp.x > 0.0) {
        touched += 1.0;
        cs += fp.x;
        let fr = min(1.0, stroke.pickupScale * fp.x * st.ds * 0.25);
        let h = sf(${F.water}u, i);
        var v = 0.0;
        if (sf(${F.bodyVol}u, i) > BODY_PRESENT) { v = sf(${F.bodyVol}u, i) * sf(${F.bodyOpen}u, i); }
        fw[u32(c) * 3u + 1u] = h * fr;
        fw[u32(c) * 3u + 2u] = v * fr;
        want += h * fr + v * fr;
      }
    }
${SG ? `    let rws = subgroupAdd(ws);
    let rcs = subgroupAdd(cs);
    let rwant = subgroupAdd(want);
    let rtouched = subgroupAdd(touched);
    if (sgLane == 0u) {
      partial[slot * 4u] = rws;
      partial[slot * 4u + 1u] = rcs;
      partial[slot * 4u + 2u] = rwant;
      partial[slot * 4u + 3u] = rtouched;
    }` : `    partial[slot * 4u] = ws;
    partial[slot * 4u + 1u] = cs;
    partial[slot * 4u + 2u] = want;
    partial[slot * 4u + 3u] = touched;`}
    workgroupBarrier();
    if (lid < 4u) {
      var acc = 0.0;
      for (var q = 0u; q < nslots; q++) { acc += partial[q * 4u + lid]; }
      sums4[lid] = acc;
    }
    workgroupBarrier();
    if (lid == 0u) {
      let a = sums4[0];
      let b = sums4[1];
      let wt = sums4[2];
      let n = sums4[3];
      st.weightSum = a;
      st.contactSum = b;
      skipFlag = select(0u, 1u, n < 0.5);
      if (skipFlag == 0u) {
        let x1 = st.x0 + st.bw - 1;
        let y1 = st.y0 + (st.cells - 1) / st.bw;
        markRect(st.x0 - 2, st.y0 - 2, x1 + 2, y1 + 2);
        // 2. Belly feeds the tip.
        let share = min(1.0, st.contactSum / st.weightSum);
        transferWC(st.belly, st.tip, min(0.9, (stroke.release * st.ds * st.cover * share) / (${f(LOAD_REACH)} * stroke.radius)));
        let room = max(0.0, WC[st.tip + 2u] - WC[st.tip] - WC[st.tip + 1u]);
        st.pickScale = select(1.0, room / wt, wt > room);
        if (wt <= 1e-12) { st.pickScale = 0.0; }
        st.tipRelease = min(0.9, st.ds * st.cover / (${f(TIP_EXCHANGE_LENGTH)} * stroke.radius));
      }
    }
    if (workgroupUniformLoad(&skipFlag) == 1u) { continue; }

    // 3. Pickup into the tip, scaled to its room (per-thread sums in registers).
    var acc18: array<f32, 18>;
    if (st.pickScale > 0.0) {
      for (var c = i32(lid); c < st.cells; c += ${BRUSH_THREADS}) {
        if (fw[u32(c) * 3u] <= 0.0) { continue; }
        let i = cellOf(c);
        let tw = fw[u32(c) * 3u + 1u] * st.pickScale;
        let h = sf(${F.water}u, i);
        if (tw > 0.0 && h > 0.0) {
          let fwr = tw / h;
          sset(${F.water}u, i, h - tw);
          acc18[0] += tw;
          for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
            let a = P[pi(0u, k, i)] * fwr;
            P[pi(0u, k, i)] -= a;
            acc18[2u + k] += a;
          }
        }
        let tb = fw[u32(c) * 3u + 2u] * st.pickScale;
        let v = sf(${F.bodyVol}u, i);
        if (tb > 0.0 && v > 0.0) {
          let fb = tb / v;
          sset(${F.bodyVol}u, i, v - tb);
          acc18[1] += tb;
          for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
            let a = P[pi(2u, k, i)] * fb;
            P[pi(2u, k, i)] -= a;
            acc18[10u + k] += a;
          }
        }
      }
    }
${SG ? `    for (var q = 0u; q < 18u; q++) {
      let v = subgroupAdd(acc18[q]);
      if (sgLane == 0u) { partial[slot * 18u + q] = v; }
    }` : `    for (var q = 0u; q < 18u; q++) { partial[slot * 18u + q] = acc18[q]; }`}
    workgroupBarrier();
    // Column q of the partials is one tip quantity: water, body, then 16 pigment amounts.
    if (lid < 18u) {
      var acc = 0.0;
      for (var q = 0u; q < nslots; q++) { acc += partial[q * 18u + lid]; }
      WC[select(st.tip + 2u + lid, st.tip + lid, lid < 2u)] += acc;
    }
    workgroupBarrier();
    if (lid < CR) { snap[lid] = WC[st.tip + lid]; }
    if (lid == 0u) { st.keep = 1.0 - st.tipRelease * st.contactSum / st.weightSum; }
    workgroupBarrier();

    // 4. Deposit weight shares of the tip snapshot.
    for (var c = i32(lid); c < st.cells; c += ${BRUSH_THREADS}) {
      let wgt = fw[u32(c) * 3u];
      if (wgt <= 0.0) { continue; }
      let i = cellOf(c);
      let frac = st.tipRelease * wgt / st.weightSum;
      let w = snap[0] * frac;
      if (w > 0.0) {
        let h = sf(${F.water}u, i) + w;
        sset(${F.water}u, i, h);
        sset(${F.depRewet}u, i, sf(${F.depRewet}u, i) + (stroke.rewet - sf(${F.depRewet}u, i)) * min(1.0, w / (h + 1e-9)));
      }
      for (var k = 0u; k < SLOTS; k++) { if (slotOn(k)) { P[pi(0u, k, i)] += snap[4u + k] * frac; } }
      let b = snap[1] * frac;
      if (b <= 1e-9) {
        for (var k = 0u; k < SLOTS; k++) { if (slotOn(k)) { P[pi(0u, k, i)] += snap[12u + k] * frac; } }
      } else {
        mixBody(i, b);
        for (var k = 0u; k < SLOTS; k++) { if (slotOn(k)) { P[pi(2u, k, i)] += snap[12u + k] * frac; } }
      }
    }
    storageBarrier();
    workgroupBarrier();

    // 5. Keep the rest, backflow, plough. (Storage barriers are needed only
    // where thread 0 touches cells owned by other threads: before ploughing
    // and between stamps. Every other phase revisits only its own cells.)
    if (lid == 0u) {
      WC[st.tip] = snap[0] * st.keep;
      WC[st.tip + 1u] = snap[1] * st.keep;
      for (var k = 0u; k < 2u * SLOTS; k++) { WC[st.tip + 4u + k] = snap[4u + k] * st.keep; }
      transferWC(st.tip, st.belly, min(0.5, (${f(TIP_BACKFLOW)} * st.ds * st.cover) / stroke.radius));
      if (stroke.plough > 0.0) {
        let xi = i32(floor(st.px));
        let yi = i32(floor(st.py));
        if (xi > 0 && yi > 0 && xi < i32(prm.W) - 1 && yi < i32(prm.H) - 1) {
          let i = u32(yi) * prm.W + u32(xi);
          if (sf(${F.bodyVol}u, i) > BODY_PRESENT) {
            let fpl = stroke.plough * st.edgeContact * st.ds * st.cover * 0.18;
            let sx = i32(round(stamps[o + 9u]));
            let sy = i32(round(stamps[o + 10u]));
            let fx = i32(round(stamps[o + 11u]));
            let fy = i32(round(stamps[o + 12u]));
            displaceBody(i, u32(i32(i) + sy * i32(prm.W) + sx), fpl * 0.4);
            displaceBody(i, u32(i32(i) - sy * i32(prm.W) - sx), fpl * 0.4);
            displaceBody(i, u32(i32(i) + fy * i32(prm.W) + fx), fpl * 0.2);
          }
        }
      }
    }
    storageBarrier();
    workgroupBarrier();
  }
  for (var q = lid; q < ${MAX_GROUPS * 2 * CARRIER_STRIDE}u; q += ${BRUSH_THREADS}u) { C[q] = WC[q]; }
}

// ---------------------------------------------------------------- palette knife (sequential, one thread)
// PaintDeposition.depositToCell: moves fraction frac of carrier (offset 0) into cell i.
fn knifeDeposit(i: u32, frac0: f32) {
  if (!(frac0 > 0.0)) { return; }
  let frac = min(1.0, frac0);
  let w = C[0] * frac;
  if (w > 0.0) { sset(${F.water}u, i, sf(${F.water}u, i) + w); C[0] -= w; }
  for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
    let a = C[4u + k] * frac;
    if (a > 0.0) { P[pi(0u, k, i)] += a; C[4u + k] -= a; }
  }
  let b = C[1] * frac;
  if (b <= 1e-9) {
    for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
      let a = C[12u + k] * frac;
      if (a > 0.0) { P[pi(0u, k, i)] += a; C[12u + k] -= a; }
    }
  } else {
    mixBody(i, b);
    C[1] -= b;
    for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
      let a = C[12u + k] * frac;
      if (a > 0.0) { P[pi(2u, k, i)] += a; C[12u + k] -= a; }
    }
  }
  if (w > 0.0) {
    sset(${F.depRewet}u, i, sf(${F.depRewet}u, i) + (stroke.rewet - sf(${F.depRewet}u, i)) * min(1.0, w / (sf(${F.water}u, i) + 1e-9)));
  }
}

@compute @workgroup_size(1)
fn knifeSteps() {
  for (var s = 0u; s < stroke.count; s++) {
    let o = s * ${KNIFE_STEP_STRIDE}u;
    let cx = stamps[o];
    let cy = stamps[o + 1u];
    let p = clamp(stamps[o + 2u], 0.0, 1.0);
    let ds = stamps[o + 3u];
    let dirX = stamps[o + 4u];
    let dirY = stamps[o + 5u];
    let ca = cos(stroke.bladeAngle);
    let sa = sin(stroke.bladeAngle);
    let bx = -dirY * ca - dirX * sa;
    let by = dirX * ca - dirY * sa;
    let half = (stroke.radius * stroke.bladeLength) / 2.0;
    let level = 0.6 * (1.0 - p) + 0.02;
    let samples = max(2u, u32(ceil(half * 2.0 / 0.7)));
    var minX = 1000000; var minY = 1000000; var maxX = -1000000; var maxY = -1000000;
    var last = 0xffffffffu;
    for (var q = 0u; q <= samples; q++) {
      let along = -half + (2.0 * half * f32(q)) / f32(samples);
      let x = i32(floor(cx + bx * along));
      let y = i32(floor(cy + by * along));
      if (x < 0 || y < 0 || x >= i32(prm.W) || y >= i32(prm.H)) { continue; }
      let i = u32(y) * prm.W + u32(x);
      if (i == last) { continue; }
      last = i;
      let bow = stroke.flex * p * (1.0 - (along / half) * (along / half)) * 0.25;
      let cellLevel = max(0.01, level - bow) + sf(${F.dryHeight}u, i);
      let top = sf(${F.bodyVol}u, i) + sf(${F.dryHeight}u, i);
      let fill = select(1.0, sf(${F.bodyOpen}u, i), sf(${F.bodyVol}u, i) > BODY_PRESENT);
      if (C[1] > 1e-6 && top <= cellLevel + 0.05) {
        let skin = min(C[1], ${f(KNIFE_SKIN)} * (0.4 + 0.6 * p) * min(1.0, ds));
        knifeDeposit(i, skin / C[1]);
      }
      if (top > cellLevel && sf(${F.bodyVol}u, i) > BODY_PRESENT) {
        let v = sf(${F.bodyVol}u, i);
        let excess = min(v, top - cellLevel) * stroke.scrape * fill * min(1.0, ds);
        let fr = min(1.0, excess / v);
        let bt = v * fr;
        sset(${F.bodyVol}u, i, v - bt);
        C[1] += bt;
        for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
          let a = P[pi(2u, k, i)] * fr;
          if (a > 0.0) { P[pi(2u, k, i)] -= a; C[12u + k] += a; }
        }
      } else if (C[1] > 1e-6) {
        let gap = cellLevel - top;
        let want = min(gap, C[1]) * stroke.spread * min(1.0, ds);
        if (want > 0.0) { knifeDeposit(i, want / max(1e-9, C[1])); }
      }
      if (sf(${F.water}u, i) > 0.01) {
        let ax = clamp(x + i32(round(dirX)), 0, i32(prm.W) - 1);
        let ay = clamp(y + i32(round(dirY)), 0, i32(prm.H) - 1);
        let j = u32(ay) * prm.W + u32(ax);
        if (j != i) {
          let wv = sf(${F.water}u, i) * 0.5;
          sset(${F.water}u, i, sf(${F.water}u, i) - wv);
          sset(${F.water}u, j, sf(${F.water}u, j) + wv);
          for (var k = 0u; k < SLOTS; k++) {
    if (!slotOn(k)) { continue; }
            let a = P[pi(0u, k, i)] * 0.5;
            P[pi(0u, k, i)] -= a;
            P[pi(0u, k, j)] += a;
          }
        }
      }
      minX = min(minX, x); minY = min(minY, y); maxX = max(maxX, x); maxY = max(maxY, y);
    }
    for (var e = 0u; e < 2u; e++) {
      let sgn = select(1.0, -1.0, e == 0u);
      let ex = i32(floor(cx + bx * half * sgn * 0.95));
      let ey = i32(floor(cy + by * half * sgn * 0.95));
      let ox = i32(floor(cx + bx * (half + 1.5) * sgn));
      let oy = i32(floor(cy + by * (half + 1.5) * sgn));
      if (ex < 0 || ey < 0 || ex >= i32(prm.W) || ey >= i32(prm.H) || ox < 0 || oy < 0 || ox >= i32(prm.W) || oy >= i32(prm.H)) { continue; }
      displaceBody(u32(ey) * prm.W + u32(ex), u32(oy) * prm.W + u32(ox), stroke.edgeRidge * min(1.0, ds));
    }
    if (maxX >= minX) { markRect(minX - 3, minY - 3, maxX + 3, maxY + 3); }
  }
}
`;
}
